import type React from 'react'
import type { IAjaxResponse, TUnit } from '@/api/interfaces'

export const kilobyte = 1024
export const megabyte = 1048576
export const FILENAME_ENCODING_REGEX = /^([^']*)'([^']*)'(.*)$/

export const isFileSizeCorrected = (fileSize: number, maxFileSize: number): boolean => fileSize / megabyte < maxFileSize

export const isValidFileExtension = <GFileExtension>(fileName: string, acceptedExtensions: GFileExtension[] | undefined): boolean =>
  Array.isArray(acceptedExtensions) ? new RegExp(`\\.(${acceptedExtensions.join('|')})$`, 'i').test(fileName) : true

export const getFileNameFromResponse = <T>(response: IAjaxResponse<T>): string | null => {
  const contentDispositionHeader = response.headers.get('content-disposition')

  if (!contentDispositionHeader) {
    return null
  }

  const parseContentDisposition = (header: string): Record<string, string> => {
    const params: Record<string, string> = {}
    let token = ''
    let key = ''
    let value = ''
    let inQuotes = false
    let isKey = true

    for (let i = 0; i < header.length; i += 1) {
      const char = header[i]

      if (char === '"') {
        inQuotes = !inQuotes
        token += char
      } else if (char === '=' && isKey && !inQuotes) {
        key = token.trim().toLowerCase()
        token = ''
        isKey = false
      } else if (char === ';' && !inQuotes) {
        if (!isKey) {
          value = token.trim()
          params[key] = value
        }
        token = ''
        isKey = true
      } else {
        token += char
      }
    }

    if (key && !isKey) {
      value = token.trim()
      params[key] = value
    }

    return params
  }

  const params = parseContentDisposition(contentDispositionHeader)

  let fileName: string | null = null

  if (params['filename*']) {
    const filenameStar = params['filename*']
    const encodingMatch = filenameStar.match(FILENAME_ENCODING_REGEX)

    if (encodingMatch) {
      const encodedFilename = encodingMatch[3]

      if (encodedFilename) {
        try {
          fileName = decodeURIComponent(encodedFilename)
        } catch (e) {
          console.error(`Error decoding filename*: ${e}`)
          fileName = encodedFilename
        }
      }
    }
  }

  if (!fileName && params['filename']) {
    fileName = params['filename']
  }

  return fileName
}

export const downloadFile = (url: string, fileName: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const downloadBlobFile = (data: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export const downloadFileFromResponseBlob = async <T>(
  response: IAjaxResponse<T>,
  responseBlob: Blob,
  fallbackFileName: string,
  tab: Window | null | undefined = undefined,
) => {
  const fileName = getFileNameFromResponse(response) || fallbackFileName

  if (response.headers.get('content-type') === 'application/pdf' && tab) {
    const obj = window.URL.createObjectURL(responseBlob)
    tab.location.href = obj
    tab.document.title = fileName
  } else {
    tab?.close()
    downloadBlobFile(responseBlob, fileName)
  }
}

export const downloadFileFromResponse = async <T>(
  response: IAjaxResponse<T>,
  fallbackFileName: string,
  tab: Window | null | undefined = undefined,
) => {
  const content = await response.blob()

  await downloadFileFromResponseBlob(response, content, fallbackFileName, tab)
}

export const getFileNameFromUrl = (file: File | string): string => {
  if (typeof file !== 'string') {
    return ''
  }

  const segments = file.split('/')
  const lastSegment = segments.pop()

  if (!lastSegment) {
    return ''
  }

  return lastSegment
}

export const formatBytes = (bytes: number, rawDecimals: number = 2): [number, TUnit] => {
  if (!bytes || isNaN(bytes)) return [0, 'Bytes']

  const decimals = rawDecimals < 0 ? 0 : rawDecimals
  const sizes: TUnit[] = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const positionOfUnit = Math.floor(Math.log(bytes) / Math.log(kilobyte))

  return [parseFloat((bytes / kilobyte ** positionOfUnit).toFixed(decimals)), sizes[positionOfUnit] as TUnit]
}

export const selectFile = (accept: string = '*/*'): Promise<File | undefined> => {
  return new Promise(resolve => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept

    input.onchange = (event: Event) => {
      const changeEvent = event as unknown as React.ChangeEvent<HTMLInputElement>

      const file = changeEvent.target.files?.[0]
      resolve(file)
    }

    input.click()
  })
}

export const base64ToBlob = (base64: string) => {
  const binaryString = window.atob(base64)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const blob = new Blob([bytes], { type: 'application/pdf' })

  const formData = new FormData()
  formData.append('document', blob, 'document.pdf')

  return formData
}

export const base64ToBlobFile = (base64: string, mime: string) => {
  const binaryString = window.atob(base64)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const byteArray = new Uint8Array(bytes)

  return new Blob([byteArray], { type: mime })
}
